import type { Role } from './types'

/**
 * Function-level capabilities.
 *
 * IMPORTANT: this table only drives the UI (showing and hiding controls).
 * It is NOT a security boundary. Every capability must be re-checked on the
 * server (Postgres RLS or API middleware) before any write.
 */
export const CAPABILITIES = [
  'schedule.view',
  'schedule.edit',
  'scenario.create',
  'scenario.edit',
  'event.create',
  'event.edit',
  'plan.create',
  'plan.approve',
  'export.run',
  'report.view',
  'settings.view',
  'settings.edit',
  'user.manage',
  'audit.view',
] as const

export type Capability = (typeof CAPABILITIES)[number]

export const CAPABILITY_LABEL: Record<Capability, string> = {
  'schedule.view': 'View schedule',
  'schedule.edit': 'Edit schedule',
  'scenario.create': 'Create scenario',
  'scenario.edit': 'Edit scenario',
  'event.create': 'Create event',
  'event.edit': 'Edit event',
  'plan.create': 'Create plan',
  'plan.approve': 'Approve plan',
  'export.run': 'Export SSIM / Excel',
  'report.view': 'View reports',
  'settings.view': 'View reference data',
  'settings.edit': 'Edit reference data',
  'user.manage': 'Manage users',
  'audit.view': 'View audit log',
}

const VIEWER: Capability[] = ['schedule.view', 'report.view', 'settings.view']

const PLANNING: Capability[] = [
  ...VIEWER,
  'schedule.edit',
  'scenario.create',
  'scenario.edit',
  'event.create',
  'event.edit',
  'plan.create',
  'export.run',
]

// Supervisor = Planning + approval + audit. No administration.
const SUPERVISOR: Capability[] = [...PLANNING, 'plan.approve', 'audit.view']

const ADMIN: Capability[] = [...CAPABILITIES]

const MATRIX: Record<Role, readonly Capability[]> = {
  viewer: VIEWER,
  planning: PLANNING,
  supervisor: SUPERVISOR,
  admin: ADMIN,
}

export function can(role: Role, capability: Capability): boolean {
  return MATRIX[role].includes(capability)
}

export function capabilitiesOf(role: Role): readonly Capability[] {
  return MATRIX[role]
}
